import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const RealtimeCtx = createContext(null);

const OFFLINE_KEY = "prestige.offline.queue.v1";
const HEARTBEAT_MS = 20000;
const BACKOFF_MIN = 500;
const BACKOFF_MAX = 15000;

function wsUrlFrom(apiBase, token) {
  const u = new URL(apiBase);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws";
  u.search = `?token=${encodeURIComponent(token)}`;
  return u.toString();
}

function readQueue() {
  try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || "[]"); }
  catch { return []; }
}
function writeQueue(q) {
  try { localStorage.setItem(OFFLINE_KEY, JSON.stringify(q.slice(-100))); }
  catch (err) { if (typeof console !== 'undefined') console.debug('contexts/RealtimeContext.jsx: swallowed', err); }
}

export function RealtimeProvider({ children }) {
  const { user, getWsTicket } = useAuth();
  const [status, setStatus] = useState("idle"); // idle|connecting|online|offline|reconnecting
  const [presence, setPresence] = useState({}); // pid -> [{user_id,name,role,since}]
  const [pending, setPending] = useState(readQueue().length);

  const wsRef = useRef(null);
  const handlersRef = useRef(new Map()); // topic -> Set<fn>
  const topicsRef = useRef(new Set());
  const backoffRef = useRef(BACKOFF_MIN);
  const hbRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedRef = useRef(false);

  const emit = useCallback((topic, event, payload) => {
    const set = handlersRef.current.get(topic);
    if (!set) return;
    set.forEach((fn) => {
      try { fn({ topic, event, payload }); } catch (e) { if (typeof console !== 'undefined') console.debug('contexts/RealtimeContext.jsx: swallowed', e); }
    });
  }, []);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  const doSubscribe = useCallback((topic) => {
    topicsRef.current.add(topic);
    send({ action: "subscribe", topic });
  }, [send]);

  const doUnsubscribe = useCallback((topic) => {
    topicsRef.current.delete(topic);
    send({ action: "unsubscribe", topic });
  }, [send]);

  const flushQueue = useCallback(async () => {
    const q = readQueue();
    if (q.length === 0) return;
    const { api } = await import("@/lib/api");
    const next = [];
    for (const item of q) {
      try {
        await api.request({ method: item.method, url: item.url, data: item.data });
      } catch (e) {
        next.push(item);
      }
    }
    writeQueue(next);
    setPending(next.length);
    if (q.length > next.length) toast.success(`Synced ${q.length - next.length} pending change${q.length - next.length === 1 ? "" : "s"}`);
  }, []);

  const connect = useCallback(async () => {
    if (!user) { setStatus("idle"); return; }
    setStatus((s) => (s === "online" ? s : "connecting"));

    // Mint a short-lived WS ticket from the httpOnly cookie session so we
    // never have to persist the primary JWT in JS-readable storage.
    const ticket = await getWsTicket();
    if (!ticket) { scheduleReconnect(); return; }

    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const url = wsUrlFrom(apiBase, ticket);
    let ws;
    try { ws = new WebSocket(url); }
    catch { scheduleReconnect(); return; }
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("online");
      backoffRef.current = BACKOFF_MIN;
      // Resubscribe topics from before the drop.
      topicsRef.current.forEach((t) => send({ action: "subscribe", topic: t }));
      // Presence for every property the user belongs to (admins get all via API)
      (user.property_ids || []).forEach((pid) => send({ action: "subscribe", topic: `presence:${pid}` }));
      // Personal channel
      send({ action: "subscribe", topic: `user:${user.id}` });
      // Heartbeat
      if (hbRef.current) clearInterval(hbRef.current);
      hbRef.current = setInterval(() => send({ action: "ping" }), HEARTBEAT_MS);
      // Flush anything queued while offline
      flushQueue();
    };

    ws.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      const { topic = "", event = "", payload = {} } = data || {};

      // Presence bookkeeping is central so any component can read `presence[pid]`
      if (topic.startsWith("presence:")) {
        const pid = topic.slice("presence:".length);
        setPresence((prev) => ({ ...prev, [pid]: payload.users || prev[pid] || [] }));
      }

      // OS-level notification for critical alerts
      if (event === "alert.new" && payload?.severity === "critical") {
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            const n = new Notification(payload.title || "Critical alert", {
              body: payload.body || "",
              icon: "/manifest.json",
              tag: `alert-${payload.id || Date.now()}`,
              requireInteraction: true,
            });
            n.onclick = () => window.focus();
          }
        } catch (err) { if (typeof console !== 'undefined') console.debug('contexts/RealtimeContext.jsx: swallowed', err); }
        toast.error(payload.title || "Critical alert", { description: payload.body });
      }

      // New notification toast (any user-scoped push)
      if (event === "notification.new" && topic === `user:${user.id}`) {
        toast(payload.title || "New notification", { description: payload.body });
      }

      emit(topic, event, payload);
      emit("*", event, payload);
    };

    ws.onerror = () => { /* swallow, onclose handles it */ };
    ws.onclose = () => {
      if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
      wsRef.current = null;
      if (closedRef.current) return;
      setStatus("reconnecting");
      scheduleReconnect();
    };
  }, [user, emit, send, flushQueue, getWsTicket]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectRef.current) return;
    const delay = Math.min(BACKOFF_MAX, backoffRef.current);
    backoffRef.current = Math.min(BACKOFF_MAX, backoffRef.current * 2);
    reconnectRef.current = setTimeout(() => {
      reconnectRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  // Kick off / tear down when auth changes.
  useEffect(() => {
    closedRef.current = false;
    if (user) connect();
    return () => {
      closedRef.current = true;
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
      if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
      if (wsRef.current) { try { wsRef.current.close(); } catch (err) { if (typeof console !== 'undefined') console.debug('contexts/RealtimeContext.jsx: swallowed', err); } }
      wsRef.current = null;
      handlersRef.current.clear();
      topicsRef.current.clear();
      setStatus("idle");
      setPresence({});
    };
  }, [user?.id, connect]);

  // Offline / online detection at the browser level.
  useEffect(() => {
    const onOffline = () => setStatus("offline");
    const onOnline = () => { if (user) connect(); };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [user, connect]);

  const subscribe = useCallback((topic, handler) => {
    if (!topic) return () => {};
    if (!handlersRef.current.has(topic)) handlersRef.current.set(topic, new Set());
    handlersRef.current.get(topic).add(handler);
    if (!topicsRef.current.has(topic)) doSubscribe(topic);
    return () => {
      const set = handlersRef.current.get(topic);
      if (set) {
        set.delete(handler);
        if (set.size === 0) {
          handlersRef.current.delete(topic);
          doUnsubscribe(topic);
        }
      }
    };
  }, [doSubscribe, doUnsubscribe]);

  const enqueueOffline = useCallback((method, url, data) => {
    const q = readQueue();
    q.push({ method, url, data, at: Date.now() });
    writeQueue(q);
    setPending(q.length);
    toast.info("Saved — will sync when you're back online");
  }, []);

  const value = useMemo(() => ({
    status, presence, pending, subscribe, enqueueOffline, flushQueue,
    isLive: status === "online",
  }), [status, presence, pending, subscribe, enqueueOffline, flushQueue]);

  return <RealtimeCtx.Provider value={value}>{children}</RealtimeCtx.Provider>;
}

export function useRealtime() {
  const ctx = useContext(RealtimeCtx);
  if (!ctx) throw new Error("useRealtime must be used inside RealtimeProvider");
  return ctx;
}

/**
 * Subscribe to a topic while a component is mounted.
 * @param topic string — e.g. `rfp:${rid}`, `property:${pid}`, `user:${uid}`
 * @param handler ({event, payload}) => void
 */
export function useSubscribe(topic, handler) {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!topic) return;
    const unsub = subscribe(topic, (msg) => ref.current && ref.current(msg));
    return unsub;
  }, [topic, subscribe]);
}
